import type { CatalogItem, FormularyItem, Route } from "@vaulted/shared";
import { DOSE_UNIT_LABELS, doseToMl, vialColor } from "@vaulted/shared";
import { type ReactNode, useId } from "react";

/** One medication in a visit's treatment, and how it's given (or a recovery service, which has none). */
export interface TreatmentLine {
  key: string;
  /** The service or add-on, and the medication: to find it on the chart. */
  itemId: string;
  formularyItemId: string;
  name: string;
  dose: string;
  ml: string;
  color: string;
  how: "bag" | "base" | "push" | "shot" | "recovery" | "other";
  route: Route | null;
  /** From an add-on, not the service. */
  addOn: boolean;
}

const fmt = (n: number) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

/** A booked visit's items in order: each service, then the add-ons that ride on it. */
export const visitItemIds = (a: { services: { serviceId: string; addOns: { id: string }[] }[] }) => a.services.flatMap((s) => [s.serviceId, ...s.addOns.map((x) => x.id)]);

/** What a visit's services and add-ons put in the bag, push, and inject, from their recipes; recovery (boots, chairs, wraps) is a line of its own. */
export function treatmentLines(itemIds: string[], items: CatalogItem[], formulary: FormularyItem[]): TreatmentLine[] {
  const out: TreatmentLine[] = [];
  for (const id of itemIds) {
    const item = items.find((x) => x.id === id);
    if (!item) continue;
    if (item.effectiveChartSection === "recovery" && !item.recipe.length) {
      out.push({ key: `${id}-recovery-${out.length}`, itemId: id, formularyItemId: "", name: item.name, dose: "", ml: "", color: "", how: "recovery", route: null, addOn: item.type === "add_on" });
      continue;
    }
    for (const r of item.recipe) {
      const f = formulary.find((x) => x.id === r.formularyItemId);
      if (!f) continue;
      const ml = doseToMl(f, r.dose);
      const how = f.kind === "fluid" ? "base" : r.route === "iv" ? "bag" : r.route === "iv_push" ? "push" : r.route === "im" || r.route === "sq" || r.route === "im_sq" ? "shot" : "other";
      out.push({
        key: `${id}-${f.id}-${out.length}`,
        itemId: id,
        formularyItemId: f.id,
        name: f.name,
        dose: f.doseUnit === "each" ? (r.dose === 1 ? "1" : fmt(r.dose)) : `${fmt(r.dose)} ${DOSE_UNIT_LABELS[f.doseUnit]}`,
        ml: f.kind === "fluid" ? (f.vialSizeMl ? `${fmt(f.vialSizeMl)} mL` : (/([\d,.]+)\s*mL/i.exec(f.name)?.[0]?.replace(/\s*ml/i, " mL") ?? "1 bag")) : ml !== null ? `${fmt(ml)} mL` : "",
        color: vialColor(f.name, f.color),
        how,
        route: r.route,
        addOn: item.type === "add_on",
      });
    }
  }
  return out;
}

const WHERE: Record<TreatmentLine["how"], string> = { bag: "In the bag", base: "The base fluid", push: "IV push", shot: "Injection", recovery: "Recovery", other: "Given" };
const ROUTE_TAG: Partial<Record<Route, string>> = { im: "IM", sq: "SQ", im_sq: "IM/SQ" };

/**
 * Tracking what's been added: `addedAt` says when each line was added to the bag (or drawn up), or
 * null; with `onToggle`, tapping a line in the list marks it. Not-yet-added ones are faded.
 */
export interface AddedTracking {
  addedAt: (l: TreatmentLine) => string | null;
  /** Lines the chart records (others, like the bag's fluid, aren't ticked off). */
  tracks: (l: TreatmentLine) => boolean;
  onToggle?: (l: TreatmentLine) => void;
  /** Tapping the bag marks everything in it at once (or clears them if all are marked). */
  onSetMany?: (lines: TreatmentLine[], added: boolean) => void;
  /** Why tapping does nothing yet (e.g. "Start the visit to mark what's added"). */
  hint?: string;
}

/** The IV bag with its additions and IV pushes on its line, injections (IM/SQ) on their own, and a list of each dose. */
export function TreatmentDrawing({
  lines,
  compact,
  tracking,
  rowExtra,
  footer,
}: {
  lines: TreatmentLine[];
  compact?: boolean;
  tracking?: AddedTracking;
  /** Under each line in the list (the visit screen: its lot, and taking it off). */
  rowExtra?: (l: TreatmentLine) => ReactNode;
  /** After the list (the visit screen: adding to the bag, or an injection). */
  footer?: ReactNode;
}) {
  const faded = (l: TreatmentLine) => !!tracking && tracking.tracks(l) && !tracking.addedAt(l);
  const tracked = tracking ? lines.filter((l) => tracking.tracks(l)) : [];
  const bag = lines.filter((l) => l.how === "bag");
  // More than one bag of fluid (an extra bag added): drawn as one bag, labelled with the count.
  const bases = lines.filter((l) => l.how === "base");
  const base = bases[0];
  const pushes = lines.filter((l) => l.how === "push");
  const shots = lines.filter((l) => l.how === "shot");
  const recovery = lines.filter((l) => l.how === "recovery");
  const hasBag = bag.length > 0 || !!base || pushes.length > 0;
  // Tapping a syringe in the drawing marks it, like tapping its line in the list.
  const onLine = tracking?.onToggle ? (l: TreatmentLine) => tracking.tracks(l) && tracking.onToggle!(l) : undefined;
  if (!lines.length && !footer) return <p className="text-sm text-muted">No recipe on this service yet (Catalog → service → Recipe), so there's nothing to draw.</p>;
  return (
    <div className={`flex flex-wrap gap-6 ${compact ? "" : "lg:gap-10"}`}>
      <div className="flex min-w-0 flex-none flex-wrap items-end gap-3 md:flex-nowrap">
        {hasBag && (
          <Bag
            bands={bag}
            base={base}
            baseCount={bases.length}
            pushes={pushes}
            small={compact}
            faded={faded}
            onBag={
              tracking?.onSetMany
                ? () => {
                    const inBag = [...bases, ...bag].filter((l) => tracking.tracks(l));
                    if (inBag.length) tracking.onSetMany!(inBag, !inBag.every((l) => tracking.addedAt(l)));
                  }
                : undefined
            }
            onLine={onLine}
          />
        )}
        {(shots.length > 0 || recovery.length > 0) && (
          <div className="mb-8 flex flex-col items-center gap-6 self-end">
            {shots.length > 0 && <Shots lines={shots} small={compact} faded={faded} onLine={onLine} />}
            {recovery.length > 0 && <Recovery lines={recovery} small={compact} done={(l) => !!tracking?.addedAt(l)} faded={faded} onLine={onLine} />}
          </div>
        )}
      </div>
      {compact && (
        // Small version (the visit pop-up): what's in it, by name and dose.
        <ul className="flex min-w-0 flex-[1_1_200px] flex-col gap-1.5 self-center text-sm">
          {lines.map((l) => (
            <li key={l.key} className="flex items-center gap-2">
              {l.how === "recovery" ? <RecoveryIcon className="size-3 shrink-0" /> : <span className="size-3 shrink-0 rounded-[3px]" style={{ background: l.color }} aria-hidden />}
              <span className="font-semibold">{l.name}</span>
              <span className="tabular-nums text-ink-soft">{l.dose}</span>
              <span className="text-muted">
                {WHERE[l.how]}
                {l.addOn ? " · add-on" : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      {!compact && (
        <ul className="flex min-w-0 flex-[1_1_320px] flex-col gap-2">
          {tracking && (
            <li className="text-sm text-muted">
              {tracking.onToggle ? "Optional: tap each one as it goes in or is drawn up (a syringe works too), or tap the bag for everything in it." : tracking.hint}
              {tracking.onToggle && (
                <span className="ml-1 font-semibold text-ink-soft">
                  {tracked.filter((l) => tracking.addedAt(l)).length} of {tracked.length} done
                </span>
              )}
            </li>
          )}
          {lines.map((l) => {
            const on = !!tracking && tracking.tracks(l);
            const at = on ? tracking!.addedAt(l) : null;
            const body = (
              <>
              {l.how === "recovery" ? (
                <span className="grid size-[18px] place-items-center rounded-[5px] bg-[#E4EEF8]" style={{ opacity: on && !at ? 0.5 : 1 }} aria-hidden>
                  <RecoveryIcon className="size-3.5" />
                </span>
              ) : (
                <span className="relative size-[18px] rounded-[5px]" style={{ background: l.color, opacity: on && !at ? 0.35 : 1 }} aria-hidden />
              )}
              <span>
                <span className="block font-bold">{l.name}</span>
                <span className="block text-sm text-muted">
                  {WHERE[l.how]}
                  {l.route && ROUTE_TAG[l.route] ? ` · ${ROUTE_TAG[l.route]}` : ""}
                  {l.addOn ? " · add-on" : ""}
                </span>
              </span>
              <span className="text-right tabular-nums">
                {/* What to draw up, big; the dose under it. */}
                <span className="block text-lg font-extrabold leading-tight">{l.ml || l.dose}</span>
                {l.ml && l.how !== "base" && l.dose !== l.ml && <span className="block text-sm text-muted">{l.dose}</span>}
              </span>
              {tracking && (
                <span
                  className={`grid size-7 ${on ? "" : "invisible"} place-items-center rounded-full border-2 text-sm font-extrabold ${at ? "border-ok bg-ok text-white" : "border-line text-transparent"}`}
                  aria-hidden
                >
                  ✓
                </span>
              )}
              </>
            );
            const box = `rounded-xl border ${at ? "border-ok/40 bg-ok-wash" : "border-line bg-white"}`;
            // With controls under it (the visit screen), the line and its controls share one box.
            const cls = `grid ${tracking ? "grid-cols-[18px_minmax(0,1fr)_auto_28px]" : "grid-cols-[18px_minmax(0,1fr)_auto]"} items-center gap-3 px-3.5 py-2.5 text-left ${rowExtra ? "rounded-xl" : box}`;
            const line = on && tracking?.onToggle ? (
              <button type="button" onClick={() => tracking.onToggle!(l)} aria-pressed={!!at} className={`${cls} w-full cursor-pointer ${rowExtra ? "hover:bg-mist/60" : "hover:border-ink-soft"}`}>
                {body}
              </button>
            ) : (
              <div className={cls}>{body}</div>
            );
            const when = at && <div className={rowExtra ? "px-3.5 pb-1 text-xs text-ok" : "mt-0.5 px-1 text-xs text-ok"}>{l.how === "push" || l.how === "shot" ? "Drawn up" : l.how === "recovery" ? "Done" : "Added"} {new Date(at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>;
            return (
              <li key={l.key}>
                {rowExtra ? (
                  <div className={box}>
                    {line}
                    {when}
                    <div className="border-t border-line/70 px-3.5 py-2">{rowExtra(l)}</div>
                  </div>
                ) : (
                  <>
                    {line}
                    {when}
                  </>
                )}
              </li>
            );
          })}
          {footer && <li className="mt-1">{footer}</li>}
        </ul>
      )}
    </div>
  );
}

/** Props that make part of a drawing tappable (mouse, touch, or keyboard). */
function tap(onTap: (() => void) | undefined, label: string) {
  if (!onTap) return {};
  return {
    role: "button",
    tabIndex: 0,
    "aria-label": label,
    className: "cursor-pointer outline-none hover:opacity-80 focus-visible:opacity-80",
    onClick: (e: React.MouseEvent) => {
      e.stopPropagation();
      onTap();
    },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onTap();
      }
    },
  };
}

function Bag({ bands, base, baseCount = 1, pushes, small, faded, onBag, onLine }: { bands: TreatmentLine[]; base: TreatmentLine | undefined; baseCount?: number; pushes: TreatmentLine[]; small?: boolean; faded: (l: TreatmentLine) => boolean; onBag?: () => void; onLine?: (l: TreatmentLine) => void }) {
  // Additions stack up from the bottom of the bag, each a band in its shelf colour; IV pushes
  // join the line below the bag at a tee.
  const bottom = 268;
  const room = 150;
  const h = bands.length ? Math.max(18, Math.min(34, room / bands.length)) : 0;
  const clip = useId();
  // IV pushes: a tee off the line under the bag, an arm out to the side, then the syringe hanging
  // down. The 2nd mirrors the 1st; the 3rd and 4th sit further out, joining the line higher up.
  const levels = Math.ceil(pushes.length / 2);
  const reach = (k: number) => 44 + k * 54;
  const width = pushes.length ? Math.max(200, 2 * (reach(levels - 1) + 36)) : 200;
  const shift = (width - 200) / 2;
  const mid = width / 2;
  const place = (i: number) => {
    const k = Math.floor(i / 2);
    const side = i % 2 === 0 ? 1 : -1;
    return { x: mid + side * reach(k), teeY: 384 - k * 18 };
  };
  const top = 404;
  const height = pushes.length ? 560 : 330;
  return (
    <figure className="m-0 flex flex-col items-center gap-1.5">
      <svg
        width={(small ? 0.6 : 0.8) * width}
        viewBox={`0 0 ${width} ${height}`}
        role={onBag || onLine ? "group" : "img"}
        aria-label={`IV bag${base ? `, ${base.name}` : ""}${bands.length ? ` with ${bands.map((b) => `${b.name} ${b.dose}`).join(", ")}` : ""}${pushes.length ? `; IV push on the line: ${pushes.map((p) => `${p.name} ${p.dose}`).join(", ")}` : ""}`}
      >
        <g transform={`translate(${shift} 0)`} {...tap(onBag, "Mark everything in the bag as added")}>
        <rect x="88" y="4" width="24" height="22" rx="4" fill="#D9D3D0" />
        <circle cx="100" cy="15" r="6" fill="#fff" stroke="#B9B1AE" strokeWidth="2" />
        <clipPath id={clip}>
          <path d="M30 30 H170 Q182 30 182 44 V250 Q182 278 154 282 L118 288 H82 L46 282 Q18 278 18 250 V44 Q18 30 30 30 Z" />
        </clipPath>
        <path d="M30 30 H170 Q182 30 182 44 V250 Q182 278 154 282 L118 288 V300 H82 V288 L46 282 Q18 278 18 250 V44 Q18 30 30 30 Z" fill="#EEF4F7" stroke="#A9B8C0" strokeWidth="3" />
        <g clipPath={`url(#${clip})`}>
          {base && <rect x="0" y="30" width="200" height="300" fill="#D6E8EF" />}
          {bands.map((b, i) => (
            <rect key={b.key} x="0" y={bottom - h * (i + 1)} width="200" height={h} fill={b.color} opacity={faded(b) ? 0.25 : 1} />
          ))}
          {bands.length > 0 && <rect x="0" y={bottom} width="200" height="40" fill={bands[0]!.color} opacity={faded(bands[0]!) ? 0.25 : 1} />}
        </g>
        {[60, 90, 120].map((y) => (
          <line key={y} x1="44" y1={y} x2="58" y2={y} stroke="#A9B8C0" strokeWidth="2" />
        ))}
        <text x="100" y="80" textAnchor="middle" fontSize="15" fontWeight="800" fill="#33464F">
          {base ? shortBag(base, baseCount) : "IV bag"}
        </text>
        <rect x="88" y="300" width="24" height="26" rx="3" fill="#D9D3D0" />
        </g>
        {pushes.length > 0 && (
          <>
            {/* The line runs on down to the client; each push tees in from the side. */}
            <line x1={mid} y1="326" x2={mid} y2={height - 14} stroke="#B9B1AE" strokeWidth="5" />
            <path d={`M${mid - 7} ${height - 18} L${mid} ${height - 6} L${mid + 7} ${height - 18} Z`} fill="#B9B1AE" />
            {pushes.map((p, i) => {
              const { x, teeY } = place(i);
              const off = faded(p) ? 0.25 : 1;
              return (
                <g key={p.key}>
                  {/* tee, arm, the 90° turn, and the drop to the syringe */}
                  <rect x={mid - 8} y={teeY - 7} width="16" height="14" rx="3" fill="#8A8285" />
                  <path d={`M${mid} ${teeY} H${x} V${top}`} fill="none" stroke="#B9B1AE" strokeWidth="5" strokeLinejoin="round" />
                  <g {...tap(onLine && (() => onLine(p)), `Mark ${p.name} as drawn up`)}>
                  <rect x={x - 22} y={top - 4} width="44" height="156" fill="transparent" />
                  <rect x={x - 5} y={top} width="10" height="10" rx="2" fill="#8A8285" />
                  <rect x={x - 14} y={top + 10} width="28" height="76" rx="5" fill="#F7F5F4" stroke="#8A8285" strokeWidth="2.5" />
                  <rect x={x - 10} y={top + 14} width="20" height="54" fill={p.color} opacity={off} />
                  <rect x={x - 10} y={top + 68} width="20" height="14" fill="#8A8285" />
                  <rect x={x - 3} y={top + 86} width="6" height="22" fill="#B9B1AE" />
                  <rect x={x - 12} y={top + 108} width="24" height="7" rx="2" fill="#B9B1AE" />
                  <text x={x} y={top + 132} textAnchor="middle" fontSize="12" fontWeight="800" fill="#2F6B20">
                    IV PUSH
                  </text>
                  <text x={x} y={top + 146} textAnchor="middle" fontSize="11" fill="#5B5456">
                    {p.name.length > 9 ? `${p.name.slice(0, 8)}…` : p.name}
                  </text>
                  </g>
                </g>
              );
            })}
          </>
        )}
      </svg>
      <figcaption className="text-sm text-muted">{pushes.length ? "IV bag and line" : "IV bag"}</figcaption>
    </figure>
  );
}

/** "LR 1000 mL", "NS 500 mL", or "IV 1000 mL": the fluid and its volume (from the bag size, else the name). */
const shortBag = (b: TreatmentLine, count = 1) => {
  const ml = /([\d,.]+)\s*mL/i.exec(b.ml)?.[1] ?? /([\d,.]+)\s*mL/i.exec(b.name)?.[1];
  const kind = /ringer/i.test(b.name) ? "LR" : /saline|nacl/i.test(b.name) ? "NS" : "IV";
  const times = count > 1 ? `${count} × ` : "";
  return ml ? `${kind} ${times}${ml} mL` : count > 1 ? `${count} IV bags` : "IV bag";
};

/** The injections: syringes side by side in one cross-section of skin and muscle (the needle reaches the muscle for IM, stops under the skin for SQ). */
function Shots({ lines, small, faded, onLine }: { lines: TreatmentLine[]; small?: boolean; faded: (l: TreatmentLine) => boolean; onLine?: (l: TreatmentLine) => void }) {
  const tagOf = (l: TreatmentLine) => (l.route && ROUTE_TAG[l.route]) ?? "IM";
  const xs = lines.map((_, i) => 30 + i * 64);
  const width = 30 + lines.length * 64 - 34;
  return (
    <figure className="m-0 flex shrink-0 flex-col items-start gap-2 self-center">
      <svg width={width * (small ? 0.35 : 0.45)} viewBox={`0 0 ${width} 170`} role={onLine ? "group" : "img"} aria-label={lines.map((l) => `${l.name} ${l.dose}, ${tagOf(l)} injection`).join("; ")}>
        {/* Skin, the layer under it, and muscle */}
        <rect x="4" y="112" width={width - 8} height="10" rx="3" fill="#E8C4A2" />
        <rect x="4" y="122" width={width - 8} height="16" fill="#F2DDBA" />
        <path d={`M4 138 H${width - 4} V156 Q${width - 4} 166 ${width - 14} 166 H14 Q4 166 4 156 Z`} fill="#D9A193" />
        {lines.map((l, i) => {
          const sq = l.route === "sq";
          return (
            <g key={l.key} transform={`translate(${xs[i]! - 100} 0)`} {...tap(onLine && (() => onLine(l)), `Mark ${l.name} as drawn up`)}>
              <rect x="68" y="0" width="64" height="150" fill="transparent" />
              <g>
                <rect x="94" y="2" width="12" height="10" rx="2" fill="#B9B1AE" />
                <rect x="98" y="12" width="4" height="12" fill="#B9B1AE" />
                <rect x="86" y="24" width="28" height="62" rx="5" fill="#F7F5F4" stroke="#8A8285" strokeWidth="2.5" />
                <rect x="90" y="28" width="20" height="10" fill="#8A8285" />
                <rect x="90" y="38" width="20" height="44" fill={l.color} opacity={faded(l) ? 0.25 : 1} />
                <rect x="95" y="86" width="10" height="8" rx="1.5" fill="#8A8285" />
                <path d={sq ? "M99 94 H101 V130 L100 133 L99 130 Z" : "M99 94 H101 V146 L100 149 L99 146 Z"} fill="#5B5456" />
              </g>
            </g>
          );
        })}
      </svg>
      <figcaption className="flex flex-col gap-1 text-sm">
        {lines.map((l) => (
          <span key={l.key} className="flex items-center gap-1.5">
            <span className="size-2.5 shrink-0 rounded-sm" style={{ background: l.color }} />
            <span className="rounded-md bg-wait-wash px-1.5 py-0.5 text-xs font-bold text-wait">{tagOf(l)}</span>
            <span className="text-muted">
              {l.name} {l.dose}
            </span>
          </span>
        ))}
      </figcaption>
    </figure>
  );
}

/** Recovery (a person leaning back): boots, chairs, wraps, rollers alike. */
function RecoveryIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} fill="none" stroke="#2F6AA3" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="13" cy="13" r="4.5" fill="#2F6AA3" stroke="none" />
      <path d="M13 19l9 9h14l5 6" />
      <path d="M6 25l8 13h22" />
      <path d="M33 9q3 3 0 6" strokeWidth="3" opacity=".7" />
      <path d="M38 6q5 6 0 12" strokeWidth="3" opacity=".5" />
    </svg>
  );
}

/** Recovery on the visit: one tile, just "Recovery" (what and how long are in the list). Green once marked done. */
function Recovery({ lines, small, done, faded, onLine }: { lines: TreatmentLine[]; small?: boolean; done: (l: TreatmentLine) => boolean; faded: (l: TreatmentLine) => boolean; onLine?: (l: TreatmentLine) => void }) {
  const all = lines.every(done);
  const dim = lines.every(faded);
  const one = lines.length === 1 ? lines[0]! : undefined;
  return (
    <figure className="m-0 flex flex-col items-center gap-1.5">
      <div
        role="img"
        aria-label={`Recovery: ${lines.map((l) => l.name).join(", ")}`}
        {...tap(one && onLine && (() => onLine(one)), `Mark ${one?.name ?? "recovery"} as done`)}
        className={`grid place-items-center rounded-[14px] border-2 ${small ? "size-11" : "size-16"} ${all ? "border-ok/50 bg-ok-wash" : "border-[#9FC0E2] bg-[#E4EEF8]"} ${one && onLine ? "cursor-pointer hover:opacity-80" : ""}`}
        style={{ opacity: dim ? 0.55 : 1 }}
      >
        <RecoveryIcon className={small ? "size-7" : "size-11"} />
      </div>
      <figcaption className="text-sm text-muted">{lines.length > 1 ? `Recovery × ${lines.length}` : "Recovery"}</figcaption>
    </figure>
  );
}
